import React, { useEffect } from 'react';
import { useHistory } from 'react-router-dom';
import FormsSpinner from 'components/common/loaders/FormSpinner';
import GlobalLayout from 'components/common/general/layouts/GlobalLayout';
import { observer } from 'mobx-react-lite';
import { toJS } from 'mobx';
import { useWindowDimensions } from '@insurancemenu/imenu-components-lib';
import RFQChecklistOptionHeader from 'components/common/headers/RFQChecklistOptionHeader';
import useRfqTemplate from 'global/hooks/useRfqTemplate';
import { useRFQProductChecklistStore } from 'store/pages/rfq/rfq-template/rfq-product-checklist/pageStore';
import { CHECKLIST_HEADER_TEST_ID } from 'components/common/rfq/constants';
import { createStyles } from './styles';
import ProductTemplateSidebar from '../../../common/rfq/ProductTemplateSidebar';
import ProductTemplateStageCard from './ProductTemplateStageCard';
import { CHECKLIST_OPTIONS_SPINNER_TEST_ID, CHECKLIST_OPTIONS_TEST_ID } from '../constants';

interface StorageParams {
  periodId: string;
  cartId: string;
  groupId: string;
}

const ProductTemplateStages = observer((): JSX.Element => {
  const classes = createStyles();

  const history = useHistory();
  const { templateId, pageType } = useRfqTemplate();
  const isBrowsingType = pageType === 'browsing';

  // Show different layout for mobile and tablet view
  const { width: windowWidth } = useWindowDimensions();
  const isMobileView = windowWidth <= 1140;

  // Get an object from local storage
  const params = localStorage.getItem('params');

  const storageParams = params
    ? (JSON.parse(params) as StorageParams)
    : ({
        periodId: '',
        cartId: '',
        groupId: '',
      } as StorageParams);

  const { isLoading, checklist, productTemplate, getProductTemplateChecklist } = useRFQProductChecklistStore();

  const convertedChecklist = toJS(checklist);
  const convertedProductTemplate = toJS(productTemplate);

  useEffect(() => {
    if (templateId) {
      void getProductTemplateChecklist(templateId);
    }
  }, []);

  if (!templateId) return <></>;

  const onRedirect = (): void => {
    switch (pageType) {
      case 'browsing':
        history.push(
          `/browsing?periodId=${storageParams.periodId}&cartId=${storageParams.cartId}&groupId=${storageParams.groupId}&productLineId=${convertedProductTemplate?.productLineId}&type=rfq`,
        );
        break;
      case 'templates':
      default:
        history.push('/product-templates');
        break;
    }
  };

  const Stages =
    convertedChecklist?.checklistOptions.map((option) => (
      <ProductTemplateStageCard
        key={`${option.card}_${option.completionStatus}`}
        option={option}
        templateId={templateId || ''}
      />
    )) || [];

  return (
    <GlobalLayout
      leftSection={<ProductTemplateSidebar />}
      rightSection={
        <div className={classes.rightSection}>
          <RFQChecklistOptionHeader
            title={isBrowsingType ? 'Build a Custom Product' : 'Build a Custom Product Template'}
            subtitle="Step by step wizard to generate a new custom product template"
            onRedirectBack={onRedirect}
            backButtonTitle={isBrowsingType && !isMobileView ? 'Back to build RFQ' : 'Back'}
            data-test-id={CHECKLIST_HEADER_TEST_ID}
          />

          <section className={classes.stagesSection} data-test-id={CHECKLIST_OPTIONS_TEST_ID}>
            {convertedChecklist && convertedChecklist.checklistOptions.length > 0 && Stages}
          </section>
          <FormsSpinner display={isLoading} data-test-id={CHECKLIST_OPTIONS_SPINNER_TEST_ID} />
        </div>
      }
    />
  );
});

export default ProductTemplateStages;
